// @mui

import { TeamMember } from 'api/models';
import { Actions } from 'components/design-system';
import BaseTable from 'components/design-system/Table/BaseTable';
import { RemoveTeamMemberConfirmationDialog } from 'components/dialogs/RemoveTeamMemberConfirmationDialog';
import useAccountIdRoute from 'hooks/useAccountIdRoute';
import useUserAccountState from 'hooks/useUserAccountState';
import { Member } from 'models/teams';
import { SyntheticEvent, useState } from 'react';
import { useDebounce } from 'react-use';
import { Avatar } from 'subframe/index';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import { getHumanReadableDate } from 'utils/subscription_plan';
import { Searchbar } from 'components/design-system/Searchbar';

interface TeamMembersTabProps {
  members: TeamMember[];
  refetchMembers: VoidFunction;
}

export default function TeamsMembers({
  members,
  refetchMembers,
}: TeamMembersTabProps) {
  const { logEvent } = AnalyticsEventLogger();
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [open, setOpen] = useState(false);
  const { user } = useUserAccountState();

  const [searchMember, setSearchMember] = useState<string>('');

  const peopleMembers = members?.filter((m) => m.user_id !== undefined);

  useDebounce(
    () => {
      if (searchMember) {
        logEvent('search-member', { searchMember });
      }
    },
    1000,
    [searchMember],
  );

  const handleOnChange = (
    _event: SyntheticEvent<Element, Event>,
    newValue: string,
  ) => {
    setSearchMember(newValue);
  };

  const membersToShow =
    searchMember !== ''
      ? members.filter((item) =>
          (item?.user_name || item?.aws_identity_arn || '')
            .toLowerCase()
            .includes(searchMember.toLowerCase()),
        )
      : members;

  return (
    <>
      <div className="w-full items-center justify-end flex">
        <Searchbar onChange={handleOnChange} className="pr-[-10px]" />
      </div>
      <BaseTable
        settings={{
          localStorageKey: 'teams_members_table',
          dataCyPrefix: 'members',
        }}
        noMatchingData={membersToShow?.length === 0}
        noMatchingDataMessage={
          'No Team Members invited yet - click "Invite Team Member" to invite someone.'
        }
        data={membersToShow || []}
        columns={[
          {
            id: 'members-table-body',
            title: 'Name',
            titleStyle: { width: '30%' },
            cellType: 'cell',

            render: (row) => {
              let avatar = (
                <Avatar variant="neutral" size="small">
                  {row?.user_name?.charAt(0)?.toUpperCase()}
                </Avatar>
              );
              if (row?.user_picture) {
                avatar = (
                  <Avatar
                    size="small"
                    image={row?.user_picture}
                    className="ph-no-capture"
                  />
                );
              } else if (row?.aws_identity_arn) {
                avatar = (
                  <Avatar
                    size="small"
                    image={'/icons/icons8-aws-logo-32.png'}
                  />
                );
              }

              return (
                <>
                  {avatar}
                  <span className="text-body font-body text-neutral-500 ph-no-capture">
                    {row?.user_name || row?.aws_identity_arn}
                  </span>
                </>
              );
            },
          },
          {
            id: '2',
            title: 'Email',
            titleStyle: { width: '40%' },
            cellType: 'text-cell',
            hideCapture: true,
            render: (row) => {
              return `${row?.user_email || 'n/a'}`;
            },
          },
          {
            id: '3',
            title: 'Added',
            titleStyle: { width: '30%' },
            cellType: 'text-cell',
            render: (row) => {
              return `${
                row ? getHumanReadableDate(row.date_added) : 'Not Found'
              }`;
            },
          },
        ]}
        actions={[
          {
            id: '4',
            render: (row) => {
              const disableActions =
                row.user_id !== undefined && peopleMembers?.length <= 1;
              return (
                <>
                  <Actions
                    sections={[
                      {
                        title: 'Team Management',
                        items: [
                          <Actions.Item
                            data-cy="remove-member-action-item"
                            key="remove-member"
                            title={
                              row?.aws_identity_arn === undefined
                                ? user?.email === row?.user_email
                                  ? 'Leave Team'
                                  : 'Remove Team Member'
                                : 'Remove AWS Identity'
                            }
                            icon={
                              user?.email === row?.user_email
                                ? 'FeatherLogOut'
                                : 'FeatherUserX'
                            }
                            disabled={disableActions}
                            onClick={() => {
                              setSelectedMember(row);
                              setOpen(true);
                            }}
                            tooltip={
                              disableActions && user?.email === row?.user_email
                                ? 'You cannot leave this team as you are the only team owner.'
                                : ''
                            }
                          />,
                        ],
                      },
                    ]}
                  />
                </>
              );
            },
          },
        ]}
      />

      <RemoveTeamMemberConfirmationDialog
        member={selectedMember || ({} as Member)}
        open={
          open &&
          (selectedMember?.user_id === undefined ||
            (peopleMembers?.length || 0) > 1)
        }
        onClose={() => {
          setOpen(false);
        }}
        callRefetch={refetchMembers}
      />
    </>
  );
}
